import { useState, useEffect } from 'react';
import { Modal, ModalFooter } from './Modal';
import { Input, Select } from './FormField';
import { Button } from '../ui/Button';
import { userApi, groupApi, teamApi, User } from '../../services/api';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User | null;
  onSuccess: () => void;
  allowedEmailDomains?: string[];
  isLastAdmin?: boolean;
}

const ROLE_OPTIONS = [
  { value: 'VIEWER', label: 'Viewer' },
  { value: 'EDITOR', label: 'Editor' },
  { value: 'ADMIN', label: 'Admin' },
];

interface Group { id: string; name: string; }
interface Team { id: string; name: string; groupId?: string | null; }

export function UserModal({
  isOpen,
  onClose,
  user,
  onSuccess,
  allowedEmailDomains = [],
  isLastAdmin = false,
}: UserModalProps) {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    title: '',
    role: 'VIEWER',
    groupId: '',
    teamId: '',
  });
  const [groups, setGroups] = useState<Group[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(user);

  useEffect(() => {
    if (!isOpen) return;
    Promise.all([groupApi.getAll(), teamApi.getAll()])
      .then(([g, t]) => { setGroups(g); setTeams(t); })
      .catch(() => {/* non-fatal */});
  }, [isOpen]);

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        name: user.name || '',
        title: user.title || '',
        role: user.role || 'VIEWER',
        groupId: user.groupId || '',
        teamId: user.teamId || '',
      });
    } else {
      setFormData({ email: '', name: '', title: '', role: 'VIEWER', groupId: '', teamId: '' });
    }
    setErrors({});
  }, [user, isOpen]);

  const availableTeams = formData.groupId
    ? teams.filter(t => t.groupId === formData.groupId)
    : teams;

  const groupOptions = [
    ...(isEditMode ? [{ value: '', label: '— None —' }] : []),
    ...groups.map(g => ({ value: g.id, label: g.name })),
  ];

  const teamOptions = [
    ...(isEditMode ? [{ value: '', label: '— None —' }] : []),
    ...availableTeams.map(t => ({ value: t.id, label: t.name })),
  ];

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    } else if (allowedEmailDomains.length > 0 && !allowedEmailDomains.some(d => formData.email.toLowerCase().endsWith(`@${d}`))) {
      const domainList = allowedEmailDomains.map(d => `@${d}`).join(', ');
      newErrors.email = `Only ${domainList} emails are allowed`;
    }
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!isEditMode) {
      if (!formData.groupId) newErrors.groupId = 'Group is required';
      if (!formData.teamId) newErrors.teamId = 'Team is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const data = {
        email: formData.email.toLowerCase().trim(),
        name: formData.name.trim(),
        title: formData.title.trim() || undefined,
        role: formData.role,
        groupId: formData.groupId || null,
        teamId: formData.teamId || null,
      };
      if (isEditMode && user) {
        const { email: _, ...updateData } = data;
        await userApi.update(user.id, updateData);
      } else {
        await userApi.create(data);
      }
      onSuccess();
      onClose();
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to save user' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    if (!confirm(`Are you sure you want to delete "${user.name}"? They will no longer be able to log in.`)) return;
    setIsSubmitting(true);
    try {
      await userApi.delete(user.id);
      onSuccess();
      onClose();
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to delete user' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditMode ? 'Edit user' : 'Add new user'} size="medium">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="john@acme.com"
          required
          error={errors.email}
          disabled={isEditMode}
          helpText={isEditMode ? 'Email cannot be changed' : undefined}
        />
        <Input
          label="Name"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="John Doe"
          required
          error={errors.name}
        />
        <Input
          label="Title"
          name="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Senior Product Manager"
          error={errors.title}
          helpText="Optional"
        />
        <Select
          label="Role"
          name="role"
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          options={ROLE_OPTIONS}
          required
          helpText={
            formData.role === 'ADMIN' ? 'Full access to all features and settings'
              : formData.role === 'EDITOR' ? 'Can view all and edit own initiatives'
              : 'Read-only access'
          }
        />
        <Select
          label="Group"
          name="groupId"
          value={formData.groupId}
          onChange={(e) => {
            const newGroupId = e.target.value;
            // Clear teamId if current team doesn't belong to new group
            const currentTeam = teams.find(t => t.id === formData.teamId);
            const teamBelongsToGroup = currentTeam && currentTeam.groupId === newGroupId;
            setFormData({ ...formData, groupId: newGroupId, teamId: teamBelongsToGroup ? formData.teamId : '' });
          }}
          options={groupOptions}
          required={!isEditMode}
          helpText={isEditMode ? 'Optional' : undefined}
          error={errors.groupId}
        />
        <Select
          label="Team"
          name="teamId"
          value={formData.teamId}
          onChange={(e) => setFormData({ ...formData, teamId: e.target.value })}
          options={teamOptions}
          required={!isEditMode}
          helpText={!formData.groupId && !isEditMode ? 'Select a group first' : (isEditMode ? (!formData.groupId ? 'Select a group first' : 'Optional') : undefined)}
          disabled={!formData.groupId}
          error={errors.teamId}
        />
        {errors.submit && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3">
            <p className="text-sm text-red-800">{errors.submit}</p>
          </div>
        )}
        <ModalFooter>
          {isEditMode && (
            <div className="mr-auto" title={isLastAdmin ? 'Cannot delete the last admin. Assign another admin first.' : undefined}>
              <Button type="button" variant="danger" onClick={handleDelete} disabled={isSubmitting || isLastAdmin}>Delete</Button>
            </div>
          )}
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : isEditMode ? 'Save changes' : 'Add user'}</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
